/**
 * Tiny "↳ DOL-42" breadcrumb rendered at the top of a delegation-child
 * kanban card, linking back to the parent task. Clicking opens the parent's
 * task detail. Hovering reveals the parent title via a native tooltip.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useCallback } from "react";
import { cn } from "@/lib/cn";
import type { TaskParentSummary } from "@/api/tasks";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";

interface TaskParentBreadcrumbProps {
  parent: TaskParentSummary;
  onOpenParent?: (parentId: string) => void;
  className?: string;
}

export function TaskParentBreadcrumb({
  parent,
  onOpenParent,
  className,
}: TaskParentBreadcrumbProps) {
  const t = useT(framesTaskboardMessages);
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the enclosing kanban-card's onClick (which would open this
      // child's detail) from firing — we want to navigate to the parent.
      e.stopPropagation();
      onOpenParent?.(parent.id);
    },
    [parent.id, onOpenParent],
  );

  const label = parent.ticket_display ?? t("parentFallback");

  return (
    <button
      type="button"
      onClick={handleClick}
      title={parent.title}
      aria-label={t("openParentTask", { ticket: label })}
      className={cn(
        "flex items-center gap-0.5 rounded px-0.5 -ml-0.5",
        "text-appkit-mini font-mono text-muted transition-colors",
        "hover:text-secondary hover:bg-elevated/60",
        "focus:outline-none focus:ring-1 focus:ring-status-selected/50",
        className,
      )}
      data-testid="task-parent-breadcrumb"
      data-parent-id={parent.id}
    >
      <span aria-hidden="true">↳</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
